import type { AdEngine } from "../../core/AdEngine";
import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";
import type { GptWrapper } from "../../core/GptWrapper";

/**
 * AdLabelPlugin
 *
 * Dynamically injects a localized "Advertisement" label into rendered ad slots.
 *
 * This plugin clones an existing `.ae-translatable-label`
 * element already translated by UCPs top_leaderboard.
 */
export class AdLabelPlugin extends AdEnginePlugin {
  private readonly translatedAdLabel: HTMLElement | null;
  private readonly excludedSlots = new Set([
    "top_leaderboard",
    "floor_adhesion",
  ]);

  constructor() {
    super();

    const label = document.querySelector(".ae-translatable-label");
    this.translatedAdLabel = label instanceof HTMLElement ? label : null;
  }

  hooks: Hooks = {
    onAdEngineInit: (adEngine: AdEngine) => {
      this.registerSlotRenderHandler(adEngine.gptWrapper);
    },
  };

  /**
   * Registers a listener to append ad labels after slot rendering.
   */
  private registerSlotRenderHandler(gpt: GptWrapper): void {
    gpt.addEventListener("slotRenderEnded", (event) => {
      const slotElementId = event.slot.getSlotElementId();

      if (this.excludedSlots.has(slotElementId)) return;

      const placeholder = document.getElementById(slotElementId);
      if (!placeholder || !this.translatedAdLabel) return;

      const labelId = `label-${slotElementId}`;
      /** Prevent duplicate labels
       *@see DigitalServicesActPlugin.ts
       */
      if (document.getElementById(labelId)) return;

      const copiedLabel = this.cloneTranslatedLabel(labelId);
      placeholder.appendChild(copiedLabel);
    });
  }

  /**
   * Clones the cached translated label and removes unnecessary elements as this one might have DSA icons attached to it.
   * @param labelId - Unique ID to assign to the new label element
   */
  private cloneTranslatedLabel(labelId: string): HTMLElement {
    const cloned = this.translatedAdLabel!.cloneNode(true) as HTMLElement;

    cloned.classList.remove("dsa-advertisement-label-clickable");
    cloned.id = labelId;

    const icon = cloned.querySelector(".dsa-advertisement-label-icon");
    icon?.remove();

    return cloned;
  }
}
